import React, { useEffect, useMemo, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Dropdown, Nav, ProgressBar, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { getUserId } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import Swal from "sweetalert2";

const MONTH_NAMES_TH = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

const DESIRABLE_LABELS = [
  'รักชาติ ศาสน์ กษัตริย์', 'ซื่อสัตย์สุจริต', 'มีวินัย', 'ใฝ่เรียนรู้',
  'อยู่อย่างพอเพียง', 'มุ่งมั่นในการทำงาน', 'รักความเป็นไทย', 'มีจิตสาธารณะ',
];

// 8 กลุ่มสาระ — เรียงตาม backend (talentController.CATEGORIES)
const TALENT_CATEGORIES = [
  { key: 'thai',    short: 'ไทย',    label: 'ภาษาไทย' },
  { key: 'english', short: 'อังกฤษ', label: 'ภาษาอังกฤษ' },
  { key: 'math',    short: 'คณิต',   label: 'คณิตศาสตร์' },
  { key: 'science', short: 'วิทย์',   label: 'วิทยาศาสตร์และเทคโนโลยี' },
  { key: 'social',  short: 'สังคม',  label: 'สังคมศึกษา' },
  { key: 'art',     short: 'ศิลป์',   label: 'ศิลปะ' },
  { key: 'pe',      short: 'พละ',    label: 'สุขศึกษาและพลศึกษา' },
  { key: 'career',  short: 'การงาน', label: 'การงานอาชีพ' },
];

const genderLabel = (room) => {
  if (!room) return null;
  if (Number(room.male) > 0 && Number(room.female) === 0)
    return { text: 'ชาย', color: 'info', icon: 'ri-men-line' };
  if (Number(room.female) > 0 && Number(room.male) === 0)
    return { text: 'หญิง', color: 'danger', icon: 'ri-women-line' };
  return null;
};

const STAT_CARDS = (room, openConcerns) => [
  { icon: 'ri-group-fill',         color: 'primary',  value: room?.total,       label: 'นักเรียนทั้งหมด'},
  { icon: 'ri-user-follow-line',   color: 'success',  value: room?.active,      label: 'กำลังศึกษา' },
  { icon: 'ri-user-unfollow-line', color: 'warning',  value: room?.withdrawn,   label: 'ออกจากการศึกษา' },
  { icon: 'ri-user-add-line',      color: 'secondary',value: room?.new_student, label: 'นักเรียนใหม่' },
  { icon: 'ri-flag-2-line',        color: 'danger',   value: openConcerns,      label: 'ถูกแจ้งดูแล (ยังไม่ยุติ)', link: '/academic/student-tracking' },
];

const ATT_CARDS = (att) => [
  { icon: 'ri-checkbox-circle-line', color: 'success', value: att?.present_count ?? 0, label: 'มาเรียน' },
  { icon: 'ri-close-circle-line',    color: 'danger',  value: att?.absent_count  ?? 0, label: 'ขาดเรียน' },
  { icon: 'ri-time-line',            color: 'warning', value: att?.late_count    ?? 0, label: 'มาสาย' },
  { icon: 'ri-file-list-3-line',     color: 'info',    value: att?.att_leave_count ?? 0, label: 'ลา' },
];

export default function AdvisorDashboard() {
  const { setLoading } = useLoading();
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (s) => {
    if (s === 'dark') {
      document.querySelectorAll('.btn-white').forEach(btn => {
        btn.classList.add('btn-outline-primary');
        btn.classList.remove('btn-white');
      });
    } else {
      document.querySelectorAll('.btn-outline-primary').forEach(btn => {
        btn.classList.remove('btn-outline-primary');
        btn.classList.add('btn-white');
      });
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const [yearList, setYearList] = useState([]);
  const [yearId, setYearId] = useState(() => localStorage.getItem('yearId') || null);
  const [selectedYear, setSelectedYear] = useState('');
  const [dashData, setDashData] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gradeSemTab, setGradeSemTab] = useState('1');
  const [periodList, setPeriodList] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [attTodayFiltered, setAttTodayFiltered] = useState(null);
  const [attLoading, setAttLoading] = useState(false);
  const [talentStudents, setTalentStudents] = useState([]);
  const [talentLoading, setTalentLoading] = useState(false);
  const [openConcerns, setOpenConcerns] = useState(0);

  // นักเรียนในห้องที่ปรึกษาที่ถูกแจ้งดูแลและยังไม่ยุติ — ใช้ summary ของระบบติดตาม
  // ซึ่ง scope ห้องของที่ปรึกษาให้ฝั่ง server แล้ว
  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`${process.env.REACT_APP_API}/tracking/summary/${yearId}`)
      .then(res => setOpenConcerns(res.data?.openConcerns ?? 0))
      .catch(() => setOpenConcerns(0));
  }, [yearId]);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`)
      .then(res => {
        const list = res.data;
        setYearList(list);
        let init = localStorage.getItem('yearId');
        if (!init && list.length > 0) {
          init = list[0].year_id;
          localStorage.setItem('yearId', init);
        }
        setYearId(String(init));
        const found = list.find(y => String(y.year_id) === String(init));
        setSelectedYear(found ? `ปีการศึกษา ${found.year_year}` : '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [setLoading]);

  useEffect(() => {
    if (!yearId) return;
    const advisorId = getUserId();
    setLocalLoading(true);
    setError(null);
    setDashData(null);
    axiosInstance.get(`${process.env.REACT_APP_API}/getAdvisorDashboardStats/${advisorId}/${yearId}`)
      .then(res => setDashData(res.data))
      .catch(err => {
        if (err.response?.status === 404) {
          setError(err.response.data?.message || 'ยังไม่ได้รับมอบหมายเป็นครูประจำชั้นในปีการศึกษานี้');
        } else {
          Swal.fire({
            title: 'เกิดข้อผิดพลาด',
            text: err.response?.data?.message || err.message,
            icon: 'error',
          });
        }
      })
      .finally(() => setLocalLoading(false));
  }, [yearId]);

  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`${process.env.REACT_APP_API}/getPeriodList/${yearId}`)
      .then(res => {
        setPeriodList(res.data);
        setSelectedPeriodId(null);
      })
      .catch(() => setPeriodList([]));
  }, [yearId]);

  useEffect(() => {
    if (!yearId) return;
    const advisorId = getUserId();
    setAttLoading(true);
    const url = selectedPeriodId
      ? `${process.env.REACT_APP_API}/getAdvisorAttendanceToday/${advisorId}/${yearId}?periodId=${selectedPeriodId}`
      : `${process.env.REACT_APP_API}/getAdvisorAttendanceToday/${advisorId}/${yearId}`;
    axiosInstance.get(url)
      .then(res => setAttTodayFiltered(res.data))
      .catch(() => setAttTodayFiltered(null))
      .finally(() => setAttLoading(false));
  }, [yearId, selectedPeriodId]);

  const roomId = dashData?.room?.r_id ?? null;

  useEffect(() => {
    if (!roomId) { setTalentStudents([]); return; }
    setTalentLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/talent/academic/room/${roomId}`)
      .then(res => setTalentStudents(res.data.students ?? []))
      .catch(() => setTalentStudents([]))
      .finally(() => setTalentLoading(false));
  }, [roomId]);

  const handleSelectYear = (id) => {
    const found = yearList.find(y => y.year_id === id);
    if (found) {
      setYearId(String(id));
      setSelectedYear(`ปีการศึกษา ${found.year_year}`);
      localStorage.setItem('yearId', id);
    }
  };

  const room = dashData?.room ?? null;
  const bmiCounts = dashData?.bmiCounts ?? {};
  const bmiMonth = dashData?.bmiMonth ?? (new Date().getMonth() + 1);
  const desirable = dashData?.desirable ?? {};
  const subjects = dashData?.subjects ?? [];
  const attMonthly = dashData?.attMonthly ?? [];
  const attWeekly = dashData?.attWeekly ?? [];
  const gradePerf = dashData?.gradePerformance ?? null;
  const today = dashData?.today ?? '';
  const attToday = attTodayFiltered ?? dashData?.attToday ?? null;

  const bmiSeries = [
    bmiCounts.underweight ?? 0,
    bmiCounts.normal      ?? 0,
    bmiCounts.overweight  ?? 0,
    bmiCounts.obese       ?? 0,
    bmiCounts.noData      ?? 0,
  ];

  const bmiDonutOptions = useMemo(() => ({
    chart: { type: 'donut' },
    colors: ['#fd7e14', '#198754', '#ffc107', '#dc3545', '#adb5bd'],
    labels: ['น้ำหนักน้อย', 'ปกติ', 'น้ำหนักเกิน', 'อ้วน', 'ไม่มีข้อมูล'],
    legend: { position: 'bottom', fontSize: '12px' },
    dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(0)}%` },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            total: {
              show: true,
              label: `เดือน ${MONTH_NAMES_TH[bmiMonth]}`,
              fontSize: '12px',
              formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0) + ' คน',
            },
          },
        },
      },
    },
    stroke: { width: 2 },
  }), [bmiMonth]);

  const desirableBarOptions = useMemo(() => ({
    chart: { type: 'bar', toolbar: { show: false } },
    colors: ['#0d6efd', '#20c997'],
    plotOptions: { bar: { columnWidth: '60%', borderRadius: 3 } },
    dataLabels: { enabled: false },
    legend: { position: 'top', fontSize: '12px' },
    xaxis: {
      categories: ['1', '2', '3', '4', '5', '6', '7', '8'],
      labels: { formatter: (val) => `ข้อ ${val}`, style: { fontSize: '11px' } },
    },
    yaxis: {
      min: 0, max: 3, tickAmount: 3,
      labels: { style: { colors: '#6e7985', fontSize: '11px' } },
    },
    tooltip: {
      shared: true, intersect: false,
      x: { formatter: (val) => DESIRABLE_LABELS[val - 1] },
    },
    grid: { borderColor: 'rgba(72,94,144,0.07)' },
  }), []);

  const desirableSeries = [
    { name: 'ภาคเรียน 1', data: desirable.sem1?.items ?? Array(8).fill(0) },
    { name: 'ภาคเรียน 2', data: desirable.sem2?.items ?? Array(8).fill(0) },
  ];

  const attMonthlySeries = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    const byMonth = {};
    attMonthly.forEach(r => { byMonth[r.att_month] = r; });
    return [
      { name: 'มาเรียน',  data: months.map(m => Number(byMonth[m]?.present ?? 0)) },
      { name: 'ขาดเรียน', data: months.map(m => Number(byMonth[m]?.absent  ?? 0)) },
      { name: 'มาสาย',    data: months.map(m => Number(byMonth[m]?.late    ?? 0)) },
      { name: 'ลา',       data: months.map(m => Number(byMonth[m]?.att_leave ?? 0)) },
    ];
  }, [attMonthly]);

  const attWeeklySeries = useMemo(() => {
    const rows = attWeekly.slice(-12);
    return [
      { name: 'มาเรียน',  data: rows.map(r => Number(r.present   ?? 0)) },
      { name: 'ขาดเรียน', data: rows.map(r => Number(r.absent    ?? 0)) },
      { name: 'มาสาย',    data: rows.map(r => Number(r.late      ?? 0)) },
      { name: 'ลา',       data: rows.map(r => Number(r.att_leave ?? 0)) },
    ];
  }, [attWeekly]);

  const attWeeklyCategories = useMemo(() =>
    attWeekly.slice(-12).map(r => {
      const d = new Date(r.week_start);
      return `${d.getDate()}/${d.getMonth() + 1}`;
    }),
  [attWeekly]);

  const attWeeklyOptions = useMemo(() => ({
    chart: { type: 'bar', toolbar: { show: false }, stacked: false },
    colors: ['#198754', '#dc3545', '#fd7e14', '#0dcaf0'],
    plotOptions: { bar: { columnWidth: '60%', borderRadius: 3 } },
    dataLabels: { enabled: false },
    legend: { position: 'top', fontSize: '12px' },
    xaxis: {
      categories: attWeeklyCategories,
      title: { text: 'สัปดาห์เริ่มต้น', style: { fontSize: '11px', fontWeight: 400 } },
      labels: { style: { fontSize: '11px' } },
    },
    yaxis: { labels: { style: { colors: '#6e7985', fontSize: '11px' } } },
    grid: { borderColor: 'rgba(72,94,144,0.07)', padding: { top: -5, bottom: 0 } },
    tooltip: {
      shared: true, intersect: false,
      x: { formatter: (val) => `สัปดาห์ ${val}` },
    },
  }), [attWeeklyCategories]);

  // ค่าเฉลี่ยต่อหมวด — เฉลี่ยเฉพาะนักเรียนที่มีคะแนนในหมวดนั้น
  const talentAverages = useMemo(() =>
    TALENT_CATEGORIES.map(c => {
      const vals = talentStudents
        .map(s => (s.talent && s.talent[c.key] ? Number(s.talent[c.key].score) : null))
        .filter(v => v != null);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    }),
  [talentStudents]);

  const hasTalentData = talentAverages.some(v => v != null);

  const talentRadarSeries = useMemo(() => [{
    name: 'คะแนนเฉลี่ยของห้อง',
    data: talentAverages.map(v => (v == null ? 0 : Number(v.toFixed(1)))),
  }], [talentAverages]);

  const talentRadarOptions = useMemo(() => ({
    chart: { type: 'radar', toolbar: { show: false } },
    colors: ['#506fd9'],
    labels: TALENT_CATEGORIES.map(c => c.short),
    stroke: { width: 2 },
    fill: { opacity: 0.2 },
    markers: { size: 4 },
    dataLabels: { enabled: false },
    yaxis: { min: 0, max: 100, tickAmount: 5, labels: { formatter: (val) => val.toFixed(0) } },
    plotOptions: {
      radar: {
        polygons: {
          strokeColors: 'rgba(72,94,144,0.15)',
          connectorColors: 'rgba(72,94,144,0.15)',
        },
      },
    },
    tooltip: { y: { formatter: (val) => `${val} คะแนน` } },
  }), []);

  const topTalent = useMemo(() => {
    let top = null, low = null;
    talentAverages.forEach((v, i) => {
      if (v == null) return;
      if (top === null || v > talentAverages[top]) top = i;
      if (low === null || v < talentAverages[low]) low = i;
    });
    // มีหมวดเดียวที่มีคะแนน = ไม่มีอะไรให้เทียบ ไม่ต้องบอกว่าควรพัฒนา
    return { top, low: low === top ? null : low };
  }, [talentAverages]);

  const attMonthlyOptions = useMemo(() => ({
    chart: { type: 'bar', toolbar: { show: false }, stacked: false },
    colors: ['#198754', '#dc3545', '#fd7e14', '#0dcaf0'],
    plotOptions: { bar: { columnWidth: '58%', borderRadius: 3 } },
    dataLabels: { enabled: false },
    legend: { position: 'top', fontSize: '12px' },
    xaxis: { categories: MONTH_NAMES_TH.slice(1), labels: { style: { fontSize: '11px' } } },
    yaxis: { labels: { style: { colors: '#6e7985', fontSize: '11px' } } },
    grid: { borderColor: 'rgba(72,94,144,0.07)', padding: { top: -5, bottom: 0 } },
    tooltip: { shared: true, intersect: false },
  }), []);

  const gradeSection = (semester) => {
    const rows = subjects.filter(s => String(s.sub_semester) === String(semester));
    if (rows.length === 0) {
      return <p className="text-secondary fs-sm mb-0">ไม่พบข้อมูลวิชา</p>;
    }
    return rows.map(item => {
      const pct = item.total_students > 0
        ? Math.round((item.submitted_count / item.total_students) * 100)
        : 0;
      return (
        <div key={item.teach_id} className="mb-2">
          <div className="d-flex justify-content-between fs-sm mb-1">
            <span>
              <span className="text-secondary me-1">{item.sub_subid}</span>
              {item.sub_subname}
              {item.sub_type === 'extra' && <span className="text-muted ms-1">(เพิ่มเติม)</span>}
            </span>
            <small className="text-secondary">{item.submitted_count}/{item.total_students}</small>
          </div>
          <ProgressBar
            now={pct}
            variant={pct === 100 ? 'success' : pct >= 50 ? 'warning' : 'danger'}
            style={{ height: 8 }}
          />
        </div>
      );
    });
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/">ที่ปรึกษาห้องเรียน</Link></li>
              <li className="breadcrumb-item active">Dashboard ห้องเรียน</li>
            </ol>
            <h4 className="main-title mb-0">
              Dashboard ห้องเรียน
              {room && (
                <span className="badge bg-primary ms-2 fs-14">{room.room_label}</span>
              )}
            </h4>
          </div>
          <Dropdown className="mt-2 mt-md-0">
            <Dropdown.Toggle variant="outline-primary" size="sm">
              <i className="ri-calendar-line me-1"></i>
              {selectedYear || 'เลือกปีการศึกษา'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map(y => (
                <Dropdown.Item
                  key={y.year_id}
                  active={String(y.year_id) === String(yearId)}
                  onClick={() => handleSelectYear(y.year_id)}
                >
                  ปีการศึกษา {y.year_year}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {localLoading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
          </div>
        )}

        {!localLoading && error && (
          <div className="alert alert-warning d-flex align-items-center gap-2">
            <i className="ri-information-line fs-18"></i>
            {error}
          </div>
        )}

        {!localLoading && dashData && (
          <>
            {/* Row 1: student stat cards */}
            <Row className="g-3 mb-3">
              {STAT_CARDS(room, openConcerns).map((card, idx) => {
                const body = (
                  <Card className="card-one h-100">
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-start gap-3">
                        <div className={`bg-${card.color} bg-opacity-15 rounded-circle p-3 flex-shrink-0`}>
                          <i className={`${card.icon} fs-22 text-${card.color}`}></i>
                        </div>
                        <div>
                          <h2 className="card-value mb-0 fs-26">{card.value ?? 0}</h2>
                          <label className="card-label">{card.label}</label>
                          {card.gender && (
                            <div className="mt-1">
                              <span className={`badge bg-${card.gender.color} bg-opacity-15 text-${card.gender.color} fs-xs fw-normal`}>
                                <i className={`${card.gender.icon} me-1`}></i>{card.gender.text}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                );
                return (
                  <Col xs="6" sm key={idx}>
                    {card.link
                      ? <Link to={card.link} className="text-decoration-none">{body}</Link>
                      : body}
                  </Col>
                );
              })}
            </Row>

            {/* Row 2: attendance today cards */}
            <Row className="g-3 mb-3">
              <Col xs="12">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-semibold fs-sm">การเข้าเรียนวันนี้</span>
                    {today && (
                      <span className="badge bg-secondary bg-opacity-15 text-dark fs-xs fw-normal">
                        {new Date(today).toLocaleDateString('th-TH', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                  {periodList.length > 0 && (
                    <div className="d-flex flex-wrap gap-1">
                      <button
                        className={`btn btn-sm ${selectedPeriodId === null ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setSelectedPeriodId(null)}
                      >
                        ทุกคาบ
                      </button>
                      {periodList.map(p => (
                        <button
                          key={p.period_id}
                          className={`btn btn-sm ${selectedPeriodId === p.period_id ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setSelectedPeriodId(p.period_id)}
                        >
                          คาบ {p.period_order}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
              {ATT_CARDS(attToday).map((card, idx) => (
                <Col xs="6" sm="3" key={idx}>
                  <Card className="card-one h-100">
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-start gap-3">
                        <div className={`bg-${card.color} bg-opacity-15 rounded-circle p-3 flex-shrink-0`}>
                          {attLoading
                            ? <span className="spinner-border spinner-border-sm" />
                            : <i className={`${card.icon} fs-22 text-${card.color}`}></i>
                          }
                        </div>
                        <div>
                          <h2 className="card-value mb-0 fs-26">{attLoading ? '—' : card.value}</h2>
                          <label className="card-label">{card.label}</label>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Row 3: grade progress + BMI donut + student performance */}
            <Row className="g-3 mb-3">
              <Col xl="6">
                <Card className="card-one h-100">
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <Card.Title as="h6" className="mb-0">ความคืบหน้าการส่งเกรด</Card.Title>
                    <Nav variant="pills" className="nav-sm gap-1">
                      {['1', '2'].map(s => (
                        <Nav.Item key={s}>
                          <Nav.Link
                            active={gradeSemTab === s}
                            onClick={() => setGradeSemTab(s)}
                            className="py-1 px-3"
                            style={{ cursor: 'pointer' }}
                          >
                            ภาคเรียน {s}
                          </Nav.Link>
                        </Nav.Item>
                      ))}
                    </Nav>
                  </Card.Header>
                  <Card.Body className="p-3" style={{ overflowY: 'auto', maxHeight: 360 }}>
                    {gradeSection(gradeSemTab)}
                  </Card.Body>
                </Card>
              </Col>

              <Col xl="3">
                <Card className="card-one h-100">
                  <Card.Header>
                    <Card.Title as="h6">
                      BMI — เดือน {MONTH_NAMES_TH[bmiMonth]}
                    </Card.Title>
                  </Card.Header>
                  <Card.Body className="p-3 d-flex flex-column align-items-center justify-content-center">
                    <ReactApexChart
                      type="donut"
                      series={bmiSeries}
                      options={bmiDonutOptions}
                      height={240}
                    />
                  </Card.Body>
                </Card>
              </Col>

              <Col xl="3">
                <Card className="card-one h-100">
                  <Card.Header>
                    <Card.Title as="h6">ประสิทธิภาพของนักเรียน</Card.Title>
                  </Card.Header>
                  <Card.Body className="p-3">
                    {gradePerf && Number(gradePerf.graded_students) > 0 ? (
                      <>
                        <h2 className="performance-value mb-0">
                          {gradePerf.avg_gpa ?? '—'}
                          <small className="text-secondary fs-sm fw-normal ms-2">เกรดเฉลี่ย</small>
                        </h2>
                        <label className="card-label fs-sm fw-medium mb-2 d-block">
                          จาก {gradePerf.graded_students} คน
                        </label>
                        <ProgressBar className="ht-8 mb-3">
                          {[
                            { key: 'excellent', variant: 'primary' },
                            { key: 'very_good', variant: 'success' },
                            { key: 'good',      variant: 'warning' },
                            { key: 'fair',      variant: 'info' },
                            { key: 'poor',      variant: 'danger' },
                            { key: 'very_poor', variant: 'secondary' },
                          ].map(({ key, variant }) => {
                            const val = Number(gradePerf[key] ?? 0);
                            const pct = gradePerf.graded_students > 0
                              ? (val / gradePerf.graded_students) * 100 : 0;
                            return pct > 0
                              ? <ProgressBar key={key} now={pct} variant={variant} />
                              : null;
                          })}
                        </ProgressBar>
                        <Table className="table-three mb-0" size="sm">
                          <tbody>
                            {[
                              { key: 'excellent', dot: 'primary', label: 'ยอดเยี่ยม (3.5+)' },
                              { key: 'very_good', dot: 'success', label: 'ดีมาก (3.0–3.5)' },
                              { key: 'good',      dot: 'warning', label: 'ดี (2.5–3.0)' },
                              { key: 'fair',      dot: 'info',    label: 'พอใช้ (2.0–2.5)' },
                              { key: 'poor',      dot: 'danger',  label: 'แย่ (1.5–2.0)' },
                              { key: 'very_poor', dot: 'secondary',label: 'แย่มาก (<1.5)' },
                            ].map(({ key, dot, label }) => {
                              const val = Number(gradePerf[key] ?? 0);
                              const pct = gradePerf.graded_students > 0
                                ? Math.round((val / gradePerf.graded_students) * 100) : 0;
                              return (
                                <tr key={key}>
                                  <td><div className={`badge-dot bg-${dot}`}></div></td>
                                  <td className="fs-xs">{label}</td>
                                  <td className="text-end fw-medium">{val}</td>
                                  <td className="text-end text-secondary fs-xs">{pct}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </>
                    ) : (
                      <p className="text-center text-secondary fs-sm mb-0">ยังไม่มีข้อมูลเกรด</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Row 4: weekly attendance bar chart + academic talent radar */}
            <Row className="g-3 mb-3">
              <Col xs="12" xl="6">
                <Card className="card-one h-100">
                  <Card.Header>
                    <Card.Title as="h6">สถิติการเข้าเรียนรายสัปดาห์ (12 สัปดาห์ล่าสุด)</Card.Title>
                  </Card.Header>
                  <Card.Body className="p-3">
                    {attWeekly.length > 0 ? (
                      <ReactApexChart
                        type="bar"
                        series={attWeeklySeries}
                        options={attWeeklyOptions}
                        height={260}
                      />
                    ) : (
                      <p className="text-center text-secondary fs-sm mb-0">ยังไม่มีข้อมูลการเข้าเรียน</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col xs="12" xl="6">
                <Card className="card-one h-100">
                  <Card.Header>
                    <Card.Title as="h6">ความถนัดทางวิชาการ (คะแนนเฉลี่ย 8 กลุ่มสาระ)</Card.Title>
                  </Card.Header>
                  <Card.Body className="p-3">
                    {talentLoading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                      </div>
                    ) : hasTalentData ? (
                      <Row className="g-3 align-items-center">
                        <Col xs="12" sm="8">
                          <ReactApexChart
                            type="radar"
                            series={talentRadarSeries}
                            options={talentRadarOptions}
                            height={260}
                          />
                        </Col>
                        <Col xs="12" sm="4">
                          <div className="d-flex flex-column gap-2">
                            {[
                              { idx: topTalent.top, color: 'success', icon: 'ri-arrow-up-line',   caption: 'จุดเด่น' },
                              { idx: topTalent.low, color: 'danger',  icon: 'ri-arrow-down-line', caption: 'ควรพัฒนา' },
                            ].map(({ idx, color, icon, caption }) => (
                              <div
                                key={caption}
                                className={`d-flex align-items-center gap-2 border border-${color} border-opacity-25 bg-${color} bg-opacity-10 rounded p-2`}
                              >
                                <i className={`${icon} fs-20 text-${color} flex-shrink-0`}></i>
                                <div style={{ minWidth: 0 }}>
                                  <label className="card-label fs-xs mb-0 d-block">{caption}</label>
                                  <span className="fw-semibold fs-sm d-block text-truncate">
                                    {idx !== null ? TALENT_CATEGORIES[idx].label : '—'}
                                  </span>
                                  {idx !== null && (
                                    <small className={`text-${color}`}>
                                      {talentAverages[idx].toFixed(1)} คะแนน
                                    </small>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </Col>
                      </Row>
                    ) : (
                      <p className="text-center text-secondary fs-sm mb-0">ยังไม่มีข้อมูลความถนัดทางวิชาการ</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Row 5: monthly attendance bar chart */}
            <Row className="g-3 mb-3">
              <Col xs="12">
                <Card className="card-one">
                  <Card.Header>
                    <Card.Title as="h6">สถิติการเข้าเรียนรายเดือน</Card.Title>
                  </Card.Header>
                  <Card.Body className="p-3">
                    {attMonthly.length > 0 ? (
                      <ReactApexChart
                        type="bar"
                        series={attMonthlySeries}
                        options={attMonthlyOptions}
                        height={260}
                      />
                    ) : (
                      <p className="text-center text-secondary fs-sm mb-0">ยังไม่มีข้อมูลการเข้าเรียน</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Row 5: desirable bar chart */}
            <Row className="g-3 mb-3">
              <Col xs="12">
                <Card className="card-one">
                  <Card.Header>
                    <Card.Title as="h6">คุณลักษณะอันพึงประสงค์ (คะแนนเฉลี่ยต่อข้อ)</Card.Title>
                  </Card.Header>
                  <Card.Body className="p-3">
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {DESIRABLE_LABELS.map((lbl, i) => (
                        <span key={i} className="badge bg-secondary bg-opacity-15 text-dark fs-xs fw-normal">
                          ข้อ {i + 1}: {lbl}
                        </span>
                      ))}
                    </div>
                    <ReactApexChart
                      type="bar"
                      series={desirableSeries}
                      options={desirableBarOptions}
                      height={260}
                    />
                    {!desirable.sem1 && !desirable.sem2 && (
                      <p className="text-center text-secondary fs-sm mt-2 mb-0">ยังไม่มีข้อมูลคุณลักษณะอันพึงประสงค์</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

        <Footer />
      </div>
    </React.Fragment>
  );
}
